import React, { useMemo } from 'react';
import { ListPlusIcon, PencilIcon, PlusIcon, ShieldAlertIcon } from 'lucide-react';
import { useAppState } from '../../contexts/AppStateContext';
import { useUi } from '../../contexts/UiContext';
import { exceptionTypeLabels, schoolKindLabels } from '../../data/labels';
import { todayItemsFor } from '../../utils/today';
import { formatDate } from '../../utils/time';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel, EmptyNote } from '../../components/ui/Panel';
import { Button } from '../../components/ui/Button';
import { StatusBadge } from '../../components/StatusBadge';
import { WeekPlanCard } from '../../components/progress/WeekPlanCard';
import { TimeLogSection } from '../../components/progress/TimeLogSection';
import { GoodiesJarCard } from '../../components/progress/GoodiesJarCard';
import { Toggle } from '../../components/ui/Form';
import { rewardEnabledFor, toggleRewardFor } from '../../utils/rewards';
export function MemberDetail({
  memberId
}: {
  memberId: string;
}) {
  const {
    family,
    familyOcc,
    db,
    memberById,
    activeMembers,
    updateReward
  } = useAppState();
  const kidIds = activeMembers.filter(m => m.role === 'otrok').map(m => m.id);
  const {
    openSub,
    openException
  } = useUi();
  const member = memberById(memberId);
  const items = useMemo(() => todayItemsFor(family, memberId, familyOcc, db.exceptions), [family, memberId, familyOcc, db.exceptions]);
  if (!member) return <EmptyNote data-id="element-1339" data-mp="2tfj61tj9bdxs">Član ne obstaja več.</EmptyNote>;
  const school = db.schoolItems.filter(s => s.familyId === family.id && s.memberId === memberId && !s.done);
  const exceptions = db.exceptions.filter(e => e.familyId === family.id && e.memberId === memberId);
  return <div data-id="element-1340" data-mp="2eqn2o9s5ap3i">
      <PageHeader title={member.name} subtitle={member.role === 'otrok' ? 'Otrok · pregled tedna' : 'Skrbnik'} onBack={() => openSub(null)} backLabel="Pregled" action={<div className="flex flex-wrap gap-2" data-id="element-1342" data-mp="37erbh460jiaf">
            <Button variant="secondary" size="sm" onClick={() => openException(memberId)} data-id="element-1343" data-mp="15i0clidjkcnp">
              <ShieldAlertIcon className="h-4 w-4" aria-hidden="true" data-id="element-1344" data-mp="1ai4sb66ml3cv" /> Dodaj izjemo
            </Button>
            <Button variant="secondary" size="sm" onClick={() => openSub({
        kind: 'settings',
        section: 'naloge'
      })} data-id="element-1345" data-mp="vt8nrj3s28h0">
              <ListPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1346" data-mp="10qs1mhwkcj8f" /> Dodeli nalogo
            </Button>
            <Button variant="secondary" size="sm" onClick={() => openSub({
        kind: 'settings',
        section: 'cilji'
      })} data-id="element-1347" data-mp="mqzgd23t2v7u">
              <PencilIcon className="h-4 w-4" aria-hidden="true" data-id="element-1348" data-mp="qv72qsmsjz2w" /> Uredi plan
            </Button>
          </div>} data-id="element-1341" data-mp="2eqvgc3w3dyfr" />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8" data-id="element-1349" data-mp="cw7hluyhm2bh">
        <div className="min-w-0 space-y-6" data-id="element-1350" data-mp="ht7lbz7v6szf">
          <WeekPlanCard memberId={memberId} data-id="element-1351" data-mp="277dtohnjwsez" />
          <Panel title="Danes" data-id="element-1352" data-mp="278f88w3rzx1g">
            {items.length === 0 ? <EmptyNote data-id="element-1353" data-mp="2c5xgsldvsa8x">Danes ni nalog.</EmptyNote> : <ul className="divide-y divide-line" data-id="element-1354" data-mp="jdhmuqpwbx9">
                {items.map(i => <li key={i.template.id} className="flex flex-wrap items-center gap-2 py-2 text-sm" data-id="element-1355" data-mp="3rpy4e29qoxoh">
                    <span className={`min-w-0 flex-1 font-semibold ${i.excused ? 'text-muted line-through' : ''}`} data-id="element-1356" data-mp="a9g7wn6gfbrw">{i.template.title}</span>
                    {i.excused ? <span className="text-xs font-bold text-excused-ink" data-id="element-1357" data-mp="5h58dlfmlrzv">Oproščeno</span> : i.occ.status === 'odprto' ? <span className="text-xs text-muted" data-id="element-1358" data-mp="j8jqzr5mfr6e">Odprto</span> : <StatusBadge status={i.occ.status} data-id="element-1359" data-mp="eba4aj9m9tm9" />}
                    {i.relation === 'predano' && <span className="text-xs font-bold text-info-ink" data-id="element-1360" data-mp="t8am9v20aa7j">opravi {memberById(i.counterpartId)?.name}</span>}
                  </li>)}
              </ul>}
          </Panel>
          <TimeLogSection memberId={memberId} data-id="element-1361" data-mp="2uzdz8qzdc0v9" />
        </div>
        <aside className="space-y-4" data-id="element-1362" data-mp="2uzxj4d8kwzbx">
          {member.role === 'otrok' && <GoodiesJarCard memberId={memberId} data-id="element-1363" data-mp="2q1m8i0agmksn" />}
          {member.role === 'otrok' && family.modules.nagrade && <Panel title={`Nagrade za ${member.name}`} hint="Kaj lahko izbira s svojimi goodies." action={<Button size="sm" variant="secondary" onClick={() => openSub({
          kind: 'settings',
          section: 'tocke'
        })} data-id="element-1365" data-mp="1r238zhncbnah">
                  <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1366" data-mp="1v3oshludqztn" /> Nova
                </Button>} bodyClassName="py-1" data-id="element-1364" data-mp="1h2ukh4b8v0vi">
              {family.rewards.length === 0 ? <p className="py-2 text-sm text-muted" data-id="element-1367" data-mp="13daja2xbabob">Katalog nagrad je prazen.</p> : <div className="divide-y divide-line" data-id="element-1368" data-mp="17croqwcqn8th">
                  {family.rewards.map(r => <Toggle key={r.id} checked={rewardEnabledFor(r, memberId)} onChange={v => updateReward(r.id, {
              availableFor: toggleRewardFor(r, memberId, kidIds),
              paused: r.paused
            }, `${r.title}: ${v ? 'na voljo' : 'ni na voljo'} za ${member.name}`)} label={r.title} hint={`${r.cost} goodies${r.paused ? ' · na pavzi za vse' : ''}`} data-id="element-1369" data-mp="1c5chdujwrfvf" />)}
                </div>}
            </Panel>}
          <Panel title="Šolski TODO" hint="Vidita otrok in skrbnika." data-id="element-1370" data-mp="3u9u4qvxsow8y">
            {school.length === 0 ? <p className="text-sm text-muted" data-id="element-1371" data-mp="3pck5ypoin88g">Nič odprtega.</p> : <ul className="space-y-2 text-sm" data-id="element-1372" data-mp="3kjr4caocgg0l">
                {school.map(s => <li key={s.id} data-id="element-1373" data-mp="3fmhfox8s48dt">
                    <b data-id="element-1374" data-mp="3fjd59i4q0gep">{schoolKindLabels[s.kind]}</b> · {s.title} <span className="text-muted" data-id="element-1375" data-mp="3bkdzxwf2uhmq">({formatDate(s.date)})</span>
                    {s.blocks.length > 0 && <span className="block text-xs text-muted" data-id="element-1376" data-mp="36jgbx37dytdb">
                        Učni bloki: {s.blocks.filter(b => b.done).length}/{s.blocks.length}
                      </span>}
                  </li>)}
              </ul>}
          </Panel>
          <Panel title="Izjeme" data-id="element-1377" data-mp="2wxx54z6so3gg">
            {exceptions.length === 0 ? <p className="text-sm text-muted" data-id="element-1378" data-mp="2i0ocm25r8tn5">Ni izjem.</p> : <ul className="space-y-1.5 text-sm" data-id="element-1379" data-mp="2myptcgfpkgvg">
                {exceptions.map(e => <li key={e.id} data-id="element-1380" data-mp="3aok8x5rewjje">
                    {exceptionTypeLabels[e.type]} · {formatDate(e.from)}–{formatDate(e.to)} ·{' '}
                    <span className="font-bold" data-id="element-1381" data-mp="3fmlmxc9zdso0">{e.status === 'caka' ? 'čaka' : e.status === 'odobreno' ? 'odobreno' : 'zavrnjeno'}</span>
                  </li>)}
              </ul>}
          </Panel>
        </aside>
      </div>
    </div>;
}